import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const SCROLL_SPEED = 70; // px per second

function MessageSegment({ messages, sx }) {
  return (
    <>
      {messages.map((msg, i) => (
        <Box
          key={i}
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            px: { xs: 4, sm: 6 },
            fontSize: { xs: '0.7rem', sm: '0.8125rem' },
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            '&::after': {
              content: '"•"',
              ml: { xs: 4, sm: 6 },
              opacity: 0.45,
              fontSize: '0.6rem',
            },
            ...sx,
          }}
        >
          {msg.trim()}
        </Box>
      ))}
    </>
  );
}

export default function AnnouncementBar({ messages, darkMode }) {
  const filtered = messages.filter((m) => m && m.trim());
  const containerRef = useRef(null);
  const segmentRef = useRef(null);
  const [segmentWidth, setSegmentWidth] = useState(0);
  const [copyCount, setCopyCount] = useState(3);

  useEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const segment = segmentRef.current;
      if (!container || !segment) return;

      const width = segment.getBoundingClientRect().width;
      const containerWidth = container.getBoundingClientRect().width;
      if (width <= 0) return;

      setSegmentWidth(width);
      setCopyCount(Math.max(3, Math.ceil((containerWidth * 2) / width) + 1));
    };

    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer && containerRef.current) observer.observe(containerRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [filtered.join('|')]);

  if (filtered.length === 0) return null;

  const duration = segmentWidth > 0 ? segmentWidth / SCROLL_SPEED : 20;

  return (
    <Box
      ref={containerRef}
      sx={{
        overflow: 'hidden',
        bgcolor: darkMode ? '#0d47a1' : '#1565c0',
        color: '#fff',
        py: { xs: 0.65, sm: 0.85 },
        borderBottom: '2px solid',
        borderColor: '#ffb300',
        direction: 'ltr',
      }}
    >
      <Box
        aria-hidden={false}
        sx={{
          display: 'inline-flex',
          whiteSpace: 'nowrap',
          willChange: 'transform',
          animation: segmentWidth > 0 ? 'dentifixMarqueeScroll linear infinite' : 'none',
          animationDuration: `${duration}s`,
          '@keyframes dentifixMarqueeScroll': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(calc(-1 * var(--marquee-shift, 0px)))' },
          },
        }}
        style={{ '--marquee-shift': `${segmentWidth}px` }}
      >
        {Array.from({ length: copyCount }, (_, index) => (
          <Box
            key={index}
            ref={index === 0 ? segmentRef : undefined}
            component="span"
            sx={{ display: 'inline-flex', flexShrink: 0 }}
          >
            <MessageSegment messages={filtered} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
